import type { UniqueIdentifier } from '@dnd-kit/core'
import { useIsReadOnly } from '@/lib/casl'
import { cn } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Grip, Star } from 'lucide-react'
import { TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'

interface VariantTabTriggerProps {
  sortableId: UniqueIdentifier
  value: string
  label: string
  isDefault?: boolean
  isActive?: boolean
}

const VariantTabTrigger = ({
  sortableId,
  value,
  label,
  isDefault = false,
  isActive = false,
}: VariantTabTriggerProps) => {
  const { isReadOnly } = useIsReadOnly()
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled: isReadOnly,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative flex shrink-0 items-center gap-0.5 rounded-full border px-1 py-1',
        isActive
          ? 'border-transparent bg-accentSecondary text-accentSecondary-foreground shadow-sm'
          : 'border-border/60 bg-muted/20 text-muted-foreground',
        isDragging && 'z-10 shadow-sm'
      )}
    >
      {!isReadOnly ? (
        <Button
          variant="ghostOnSurface"
          size="icon-3xs"
          ref={setActivatorNodeRef}
          aria-label={`Reorder ${label}`}
          {...attributes}
          {...listeners}
        >
          <Grip className="size-3" />
        </Button>
      ) : null}

      <TabsTrigger value={value} align="start" className="min-w-0">
        <span className="max-w-[10rem] truncate">{label}</span>
        {isDefault ? <Star className="ml-1 size-3 text-interaction" /> : null}
      </TabsTrigger>
    </div>
  )
}

export default VariantTabTrigger
